import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';

export function PiecesJointes({
  type,
  id,
  ecriture,
}: {
  type: 'incident' | 'intervention' | 'signalement' | 'equipement' | 'cout' | 'investissement';
  id: string;
  ecriture: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const client = useQueryClient();
  const cle = ['pieces-jointes', type, id];
  const chemin = `/pieces-jointes/objets/${type}/${id}`;
  const liste = useQuery({
    queryKey: cle,
    queryFn: () => requete<{ elements: { id: string; nom: string; taille: number }[] }>(chemin),
    enabled: ouvert,
  });
  return (
    <section aria-label="Pièces jointes">
      <button type="button" aria-expanded={ouvert} onClick={() => setOuvert(!ouvert)}>
        Pièces jointes
      </button>
      {ouvert && (
        <>
          <p>JPEG, PNG, WebP ou PDF, 5 Mio par fichier. Maximum 20 fichiers et 50 Mio par objet.</p>
          {ecriture && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new FormData(form);
                const fichier = data.get('fichier');
                setErreur('');
                if (!(fichier instanceof File) || !fichier.size || fichier.size > 5 * 1024 * 1024) {
                  setErreur('Choisissez un fichier non vide de 5 Mio maximum');
                  return;
                }
                setEnCours(true);
                try {
                  await requete(chemin, { methode: 'POST', corps: data });
                  form.reset();
                  await client.invalidateQueries({ queryKey: cle });
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setEnCours(false);
                }
              }}
            >
              <label>
                Fichier à joindre{' '}
                <input
                  type="file"
                  name="fichier"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  required
                />
              </label>
              <button disabled={enCours}>Joindre le fichier</button>
            </form>
          )}
          {(erreur || liste.error) && <p role="alert">{erreur || messageErreur(liste.error)}</p>}
          {liste.isPending && <p>Chargement…</p>}
          <ul>
            {liste.data?.elements.map((p) => (
              <li key={p.id}>
                <button
                  disabled={enCours}
                  type="button"
                  onClick={async () => {
                    setErreur('');
                    setEnCours(true);
                    try {
                      const r = await requete<Response>(`/pieces-jointes/${p.id}`, { brut: true });
                      const url = URL.createObjectURL(await r.blob());
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = p.nom;
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    } catch (err) {
                      setErreur(messageErreur(err));
                    } finally {
                      setEnCours(false);
                    }
                  }}
                >
                  Télécharger {p.nom}
                </button>{' '}
                — {Math.ceil(p.taille / 1024)} Kio
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
